import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  createKinematicSetup,
  createKinematicSetupSuccess,
  createKinematicSetupFailure,
  listKinematicSetups,
  listKinematicSetupsSuccess,
  listKinematicSetupsFailure,
  selectKinematicSetup,
  selectKinematicSetupSuccess,
  selectKinematicSetupFailure,
  deleteKinematicSetup,
  deleteKinematicSetupSuccess,
  deleteKinematicSetupFailure,
  setLoadInactiveKinematicSetups,
  updateKinematicSetup,
  updateKinematicSetupSuccess,
  updateKinematicSetupFailure, setKinematicSetupActiveState, setKinematicSetupActiveStateSuccess,
  setKinematicSetupActiveStateFailure,
} from '@state/kinematic-setups/kinematic-setup.actions';
import { KinematicSetup, KinematicSetupBase } from '@state/kinematic-setups/kinematic-setup.interfaces';
import {
  selectInactiveKinematicSetupsLoaded,
  selectLoadInactiveKinematicSetups,
} from '@state/kinematic-setups/kinematic-setup.selectors';
import { concatMap, mergeMap, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';


@Injectable()
export class KinematicSetupEffects {
  actions$ = inject(Actions);
  store = inject(Store);
  api = inject(ApiService);
  handler = inject(EffectHandlerService);

  loadInactive = this.store.selectSignal(selectLoadInactiveKinematicSetups);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(createKinematicSetup),
    mergeMap(({ data }) => this.api.createKinematicSetup(data).pipe(
      this.handler.effectResponse({
        success: (createdSetup: KinematicSetup) => createKinematicSetupSuccess({ createdSetup }),
        successMessage: 'Creation successful',
        retry: createKinematicSetup({ data }),
        failure: createKinematicSetupFailure(),
        failureMessage: 'Create kinematic setup failed'
      })
    ))
  ));

  list$ = createEffect(() => this.actions$.pipe(
    ofType(listKinematicSetups),
    switchMap(() => this.api.listKinematicSetups(!this.loadInactive()).pipe(
      this.handler.effectResponse({
        success: (setups: KinematicSetupBase[]) => listKinematicSetupsSuccess({ setups }),
        retry: listKinematicSetups(),
        failure: listKinematicSetupsFailure(),
        failureMessage: 'Load kinematic setups failed'
      })
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(selectKinematicSetup),
    switchMap(({ uuid }) => this.api.retrieveKinematicSetup(uuid).pipe(
      this.handler.effectResponse({
        success: (selected: KinematicSetup) => selectKinematicSetupSuccess({ selected }),
        retry: selectKinematicSetup({ uuid }),
        failure: selectKinematicSetupFailure(),
        failureMessage: 'Load kinematic setups failed'
      })
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(updateKinematicSetup),
    concatMap(({ setup }) => this.api.updateKinematicSetup(setup).pipe(
      this.handler.effectResponse({
        success: (updatedSetup: KinematicSetup) => updateKinematicSetupSuccess({ updatedSetup }),
        successMessage: 'Save successful',
        retry: updateKinematicSetup({ setup }),
        failure: updateKinematicSetupFailure(),
        failureMessage: 'Update kinematic setup failed'
      })
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteKinematicSetup),
    mergeMap(({ uuid }) => this.api.deleteKinematicSetup(uuid).pipe(
      this.handler.effectResponse({
        success: () => deleteKinematicSetupSuccess({ uuid }),
        successMessage: 'Deletion successful',
        retry: deleteKinematicSetup({ uuid }),
        failure: deleteKinematicSetupFailure(),
        failureMessage: 'Delete kinematic setup failed'
      })
    ))
  ));

  // Trigger a new load when load inactive is first set to true, otherewise when toggles simple filter existing results
  setLoadInactive$ = createEffect(() => this.actions$.pipe(
    ofType(setLoadInactiveKinematicSetups),
    concatLatestFrom(() => this.store.select(selectInactiveKinematicSetupsLoaded)),
    filter(([{ loadInactive }, inactiveLoaded]) => loadInactive && !inactiveLoaded),
    map(() => listKinematicSetups())
  ));

  setActiveState$ = createEffect(() => this.actions$.pipe(
    ofType(setKinematicSetupActiveState),
    switchMap(({ uuid, active }) => this.api.setKinematicSetupActiveState(uuid, active).pipe(
      this.handler.effectResponse({
        success: (updatedSetup: KinematicSetupBase) => setKinematicSetupActiveStateSuccess({ updatedSetup }),
        retry: setKinematicSetupActiveState({ uuid, active }),
        failure: setKinematicSetupActiveStateFailure(),
        failureMessage: `Failed to set Kinematic Setup as ${active ? 'inactive' : 'active'}`,
      })
    ))
  ));

}
